import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { IslamicPattern } from "./ui/IslamicPattern";
import { LiveUserCount } from "./LiveUserCount";
import appMockup from "@/assets/app-mockup.png";

interface CoverPageProps {
  onStart: () => void;
  onResume?: () => void;
  onPreview?: () => void;
  onOpenNewsletter?: () => void;
  onOpenPrivacy?: () => void;
  onOpenTerms?: () => void;
}

export const CoverPage = ({
  onStart,
  onResume,
  onPreview,
  onOpenNewsletter,
  onOpenPrivacy,
  onOpenTerms
}: CoverPageProps) => {
  const [isReturning, setIsReturning] = useState(false);

  useEffect(() => {
    const hasStartDate = localStorage.getItem('ramadanStartDate');
    setIsReturning(!!hasStartDate);
  }, []);

  return <div className="min-h-screen w-full gradient-cover flex flex-col items-center justify-center text-center px-6 relative overflow-hidden shadow-none rounded-none">
      {/* Islamic geometric pattern — softer, larger scale */}
      <IslamicPattern opacity={0.08} className="blur-[0.5px]" />

      {/* Vignette: stronger to frame content */}
      <div className="absolute inset-0 pointer-events-none" style={{
      background: 'radial-gradient(ellipse at center, transparent 0%, hsla(155, 55%, 8%, 0.55) 100%)'
    }} />

      {/* Soft dark overlay behind central content for readability */}
      <div className="absolute inset-0 pointer-events-none" style={{
      background: 'radial-gradient(ellipse 60% 70% at 50% 45%, hsla(155, 40%, 6%, 0.35) 0%, transparent 100%)'
    }} />

      {/* Soft moonlight glow behind text — spiritual calm */}
      <div className="absolute inset-0 pointer-events-none" style={{
      background: 'radial-gradient(ellipse 40% 35% at 50% 42%, hsla(45, 50%, 65%, 0.07) 0%, transparent 70%)'
    }} />

      {/* Content */}
      <motion.div className="relative z-10 max-w-md" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 1.2
    }}>
        {/* Crescent symbol */}
        <motion.div className="text-4xl mb-10" initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.5
      }} style={{
        color: 'hsl(45, 70%, 55%)'
      }}>
          ☽
        </motion.div>

        {/* Title */}
        <motion.h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-medium tracking-tight leading-[1.1] mb-6" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4,
        duration: 0.8
      }} style={{
        color: 'hsl(30, 30%, 98%)'
      }}>
          Layali
        </motion.h1>

        {/* Core promise */}
        <motion.p className="text-lg md:text-xl leading-relaxed max-w-sm mx-auto" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.8,
        duration: 1.2
      }} style={{
        color: 'hsl(150, 20%, 85%)'
      }}>
          A guided 30-day space for duʿāʾ, reflection, and remembrance through the blessed month.
        </motion.p>

        {/* Emotional anchor */}
        <motion.p className="text-sm md:text-base mt-4 tracking-wide max-w-xs mx-auto" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 1.2,
        duration: 1.2
      }} style={{
        color: 'hsl(45, 50%, 70%)'
      }}>A product powered by
Dua and Affirmations.
      </motion.p>

        <motion.p className="text-xs md:text-sm mt-3 tracking-wide max-w-xs mx-auto" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 1.5,
        duration: 1.2
      }} style={{
        color: 'hsl(150, 20%, 70%)'
      }}>
          Helping You to Transform Your Heart Through the Blessed Month.
        </motion.p>

        {/* Breathing space */}
        <motion.div className="h-8 md:h-10" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4 }} />

        {/* CTA Buttons */}
        {isReturning ? (
          <>
            {/* Resume Journey — primary gold button */}
            <motion.button onClick={onResume} className="px-8 py-4 text-sm font-medium uppercase tracking-[0.18em] rounded-sm
                         transition-all duration-700" style={{
            color: 'hsl(30, 30%, 98%)',
            backgroundColor: 'hsl(45, 55%, 45%)',
            boxShadow: 'inset 0 1px 0 hsla(45, 60%, 70%, 0.15), 0 2px 8px hsla(45, 55%, 30%, 0.2)'
          }} initial={{
            opacity: 0,
            y: 6
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 1.8,
            duration: 1.0
          }} whileHover={{
            y: -1,
            backgroundColor: 'hsl(45, 55%, 48%)',
            boxShadow: 'inset 0 1px 0 hsla(45, 60%, 70%, 0.2), 0 4px 12px hsla(45, 55%, 30%, 0.25)'
          }} whileTap={{
            scale: 0.98,
            y: 0
          }}>
              Resume Journey
            </motion.button>

            {/* Start a new journey — muted secondary link */}
            <motion.button onClick={onStart} className="mt-4 text-xs tracking-[0.12em] uppercase transition-all duration-400 hover:opacity-100 hover:underline underline-offset-4 decoration-[0.5px] block mx-auto" style={{
        color: 'hsl(150, 15%, 65%)'
          }} initial={{
            opacity: 0
          }} animate={{
            opacity: 0.8
          }} transition={{
            delay: 2.2,
            duration: 1.0
          }}>
              Start a new journey
            </motion.button>
          </>
        ) : (
          /* Begin Journey — original button for new users */
          <motion.button onClick={onStart} className="px-8 py-4 text-sm font-medium uppercase tracking-[0.18em] rounded-sm
                       transition-all duration-700" style={{
          color: 'hsl(30, 30%, 98%)',
          backgroundColor: 'hsl(45, 55%, 45%)',
          boxShadow: 'inset 0 1px 0 hsla(45, 60%, 70%, 0.15), 0 2px 8px hsla(45, 55%, 30%, 0.2)'
        }} initial={{
          opacity: 0,
          y: 6
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 1.8,
          duration: 1.0
        }} whileHover={{
          y: -1,
          backgroundColor: 'hsl(45, 55%, 48%)',
          boxShadow: 'inset 0 1px 0 hsla(45, 60%, 70%, 0.2), 0 4px 12px hsla(45, 55%, 30%, 0.25)'
        }} whileTap={{
          scale: 0.98,
          y: 0
        }}>
            Begin Journey
          </motion.button>
        )}

        {/* Preview link */}
        {onPreview && (
          <motion.button
            onClick={onPreview}
            className="mt-5 text-xs tracking-[0.12em] uppercase transition-all duration-400 hover:opacity-100 hover:underline underline-offset-4 decoration-[0.5px] block mx-auto"
            style={{ color: 'hsl(45, 50%, 70%)' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.9 }}
            transition={{ delay: 2.0, duration: 1.0 }}
          >
            ✦ Preview the App
          </motion.button>
        )}

        {/* Secondary links — quiet, understated */}
        <motion.div className="mt-8 flex flex-col items-center gap-2" initial={{
        opacity: 0
      }} animate={{
       opacity: 0.8
      }} transition={{
        delay: 2.4,
        duration: 1.0
      }}>
          <a href="https://mailchi.mp/5fe845f9a36b/dua-and-affirmations" target="_blank" rel="noopener noreferrer" className="text-xs tracking-[0.12em] uppercase transition-all duration-400 hover:opacity-100 hover:underline underline-offset-4 decoration-[0.5px]" style={{
          color: 'hsl(150, 15%, 65%)'
        }}>
            Subscribe to Newsletter
          </a>

          {onOpenNewsletter && <button onClick={onOpenNewsletter} className="text-xs tracking-[0.12em] uppercase transition-all duration-400 hover:opacity-100 hover:underline underline-offset-4 decoration-[0.5px]" style={{
          color: 'hsl(150, 15%, 65%)'
        }}>
            Read the Newsletter
          </button>}

          <a
            href="https://buy.stripe.com/bJecN59MH0xv79x5xh14400"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-medium tracking-[0.14em] uppercase transition-all duration-400 hover:opacity-100 hover:underline underline-offset-4 decoration-[0.5px]"
            style={{ color: 'hsl(45, 60%, 72%)' }}
          >
            ♡ Support Layali
          </a>
        </motion.div>

        {/* Live user count */}
        <motion.div className="mt-6 flex justify-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.6, duration: 1.0 }}>
          <LiveUserCount />
        </motion.div>

        {/* Footer legal links */}
        <motion.div className="mt-4 flex items-center justify-center gap-3" initial={{ opacity: 0 }} animate={{ opacity: 0.8 }} transition={{ delay: 2.8, duration: 1.0 }}>
          {onOpenPrivacy &&
        <button onClick={onOpenPrivacy} className="text-[11px] tracking-[0.1em] uppercase transition-all duration-400 hover:opacity-100 hover:underline underline-offset-4 decoration-[0.5px]" style={{ color: 'hsl(150, 20%, 65%)' }}>
              Privacy
            </button>
        }
          <span className="text-[11px]" style={{ color: 'hsl(150, 15%, 45%)' }}>·</span>
          {onOpenTerms &&
        <button onClick={onOpenTerms} className="text-[11px] tracking-[0.1em] uppercase transition-all duration-400 hover:opacity-100 hover:underline underline-offset-4 decoration-[0.5px]" style={{ color: 'hsl(150, 20%, 65%)' }}>
              Terms
            </button>
        }
        </motion.div>

        {/* App Mockup Image */}
        <motion.div
        className="mt-16 w-full max-w-2xl mx-auto"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.5, duration: 0.8 }}>
        </motion.div>
      </motion.div>
    </div>;
};
